import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users, Euro, TrendingUp, Share2, Copy, Check, Plus, Trash2,
  Briefcase, Sparkles, Banknote,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DashboardShell } from '../components/dashboard/DashboardShell';

const API = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUS_LABELS = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  contacted: { label: 'Contacté', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  interview: { label: 'Entretien', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  signed: { label: 'Signé', color: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700 border-red-200' },
  lost: { label: 'Perdu', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const COMMISSION_STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  due: { label: 'À payer', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700 border-green-200' },
  cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const SectionTitle = ({ kicker, title, em }) => (
  <div className="mb-6">
    <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">{kicker}</p>
    <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
      {title} {em && <span className="font-serif italic text-[#C9B896]">{em}</span>}
    </h2>
  </div>
);

export default function ApporteurDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    course_id: '', message: '', current_situation: '', professional_goal: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, leadsRes, commRes, coursesRes] = await Promise.all([
        axios.get(`${API}/api/apporteur/me`),
        axios.get(`${API}/api/apporteur/leads`),
        axios.get(`${API}/api/apporteur/commissions`),
        axios.get(`${API}/api/courses?course_type=onsite`),
      ]);
      setMe(meRes.data);
      setLeads(leadsRes.data);
      setCommissions(commRes.data);
      setCourses(coursesRes.data);
    } catch (e) {
      toast.error('Impossible de charger ton tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({
    first_name: '', last_name: '', email: '', phone: '',
    course_id: '', message: '', current_situation: '', professional_goal: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error('Prénom, nom et email sont obligatoires');
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.course_id) delete payload.course_id;
      await axios.post(`${API}/api/apporteur/leads`, payload);
      toast.success('Candidat ajouté avec succès');
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Supprimer ${lead.first_name} ${lead.last_name} ?`)) return;
    try {
      await axios.delete(`${API}/api/apporteur/leads/${lead.id}`);
      toast.success('Supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleCopy = async () => {
    if (!me?.profile?.share_code) return;
    const link = `${window.location.origin}/devenir-apporteur?ref=${me.profile.share_code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#4A5568]">Chargement…</p>
        </div>
      </div>
    );
  }

  const { profile, stats } = me;
  const ratePercent = Math.round((stats.commission_rate || 0.1) * 100);

  // Sidebar top: share code chip
  const TopSlot = (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-1 flex items-center gap-1.5">
        <Share2 className="w-3 h-3" /> Code partenaire
      </p>
      <p className="font-serif italic text-2xl text-[#1F2A4A] tracking-wide mb-3">{profile.share_code}</p>
      <Button onClick={handleCopy} size="sm" className="w-full bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full text-xs">
        {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
        {copied ? 'Copié' : 'Copier le lien'}
      </Button>
    </div>
  );

  // ────────────────────────────────────── Section content
  const Overview = (
    <div className="space-y-6">
      <SectionTitle kicker="VUE D'ENSEMBLE" title="Tes chiffres" em="en un coup d'œil." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { Icon: Users, label: 'Candidats', value: stats.leads_total, sub: 'au total' },
          { Icon: Sparkles, label: 'Signés', value: stats.leads_signed, sub: 'ont rejoint' },
          { Icon: Banknote, label: 'En attente', value: `${stats.commissions_pending_eur.toFixed(0)} €`, sub: 'commissions à venir' },
          { Icon: TrendingUp, label: 'Total perçu', value: `${stats.commissions_paid_eur.toFixed(0)} €`, sub: 'commissions versées' },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid={`stat-${i}`}>
            <s.Icon className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none mb-1">{s.value}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">{s.label}</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>
      {leads.length > 0 && (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4">Activité récente</p>
          <ul className="divide-y divide-[#1F2A4A]/8">
            {leads.slice(0, 5).map((l) => {
              const st = LEAD_STATUS_LABELS[l.status] || LEAD_STATUS_LABELS.new;
              return (
                <li key={l.id} className="py-3 flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-[#C9B896]/15 flex items-center justify-center text-[#1F2A4A] text-xs font-medium">
                    {l.first_name.charAt(0)}{l.last_name.charAt(0)}
                  </div>
                  <span className="flex-1 text-[#1F2A4A] truncate">{l.first_name} {l.last_name}</span>
                  <Badge className={`${st.color} border text-[10px]`}>{st.label}</Badge>
                  <span className="text-xs text-[#4A5568]/70 hidden sm:inline">{format(parseISO(l.created_at), 'd MMM', { locale: fr })}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );

  const LeadsContent = (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <SectionTitle kicker="MES CANDIDATS" title="Suivi" em="des candidatures." />
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full" data-testid="new-lead-btn">
              <Plus className="w-4 h-4 mr-2" /> Déposer une candidature
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle candidature</DialogTitle>
              <DialogDescription>L'école contactera ton candidat sous 48 heures.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prénom *</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="lead-first-name" /></div>
                <div><Label>Nom *</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="lead-last-name" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <Label>Formation visée</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="lead-course"><SelectValue placeholder="Sélectionner une formation" /></SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.title} ({c.price.toFixed(0)} €)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Situation actuelle</Label><Input value={form.current_situation} onChange={(e) => setForm({ ...form, current_situation: e.target.value })} placeholder="En reconversion" /></div>
              <div><Label>Objectif professionnel</Label><Input value={form.professional_goal} onChange={(e) => setForm({ ...form, professional_goal: e.target.value })} placeholder="Ouvrir un atelier" /></div>
              <div><Label>Message / notes</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} /></div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-[#1F2A4A] text-white hover:bg-[#324166]" data-testid="lead-submit">Envoyer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#1F2A4A]/15 rounded-xl">
          <Users className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
          <p className="text-[#4A5568]">Aucune candidature pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => {
            const status = LEAD_STATUS_LABELS[l.status] || LEAD_STATUS_LABELS.new;
            return (
              <div key={l.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-4 flex items-center gap-4" data-testid={`lead-row-${l.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#C9B896]/15 flex items-center justify-center flex-shrink-0 text-[#1F2A4A] font-medium">
                  {l.first_name.charAt(0)}{l.last_name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1F2A4A] truncate">{l.first_name} {l.last_name}</p>
                  <p className="text-xs text-[#4A5568] truncate">{l.email}{l.phone ? ` · ${l.phone}` : ''}</p>
                  {l.course_title && <p className="text-xs text-[#C9B896] mt-1">→ {l.course_title} ({l.course_price.toFixed(0)} €)</p>}
                </div>
                <Badge className={`${status.color} border`}>{status.label}</Badge>
                <span className="text-xs text-[#4A5568]/70 hidden md:inline">{format(parseISO(l.created_at), 'd MMM', { locale: fr })}</span>
                {l.status !== 'signed' && (
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(l)} className="text-red-600 hover:bg-red-50" data-testid={`delete-lead-${l.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const CommissionsContent = (
    <div className="space-y-5">
      <SectionTitle kicker="COMMISSIONS" title="Tes" em="rémunérations." />
      {commissions.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#1F2A4A]/15 rounded-xl">
          <Banknote className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
          <p className="text-[#4A5568]">Aucune commission pour le moment.</p>
          <p className="text-[#4A5568] text-sm mt-2">Tu toucheras une commission dès qu'un candidat signera sa formation.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {commissions.map((c) => {
            const status = COMMISSION_STATUS_LABELS[c.status] || COMMISSION_STATUS_LABELS.pending;
            return (
              <div key={c.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-4 flex items-center gap-4" data-testid={`commission-row-${c.id}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C9B896] to-[#E0D4BA] flex items-center justify-center flex-shrink-0">
                  <Euro className="w-5 h-5 text-[#1F2A4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1F2A4A] truncate">{c.course_title || '—'}</p>
                  <p className="text-xs text-[#4A5568]">Base {c.base_amount_ht.toFixed(0)} € HT · taux {Math.round(c.rate * 100)} % · créée le {format(parseISO(c.created_at), 'd MMM yyyy', { locale: fr })}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif italic text-2xl text-[#1F2A4A] leading-none">{c.amount_ht.toFixed(0)} €</p>
                  <p className="text-[10px] text-[#C9B896] font-mono uppercase tracking-[0.2em] mt-1">HT</p>
                </div>
                <Badge className={`${status.color} border`}>{status.label}</Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const ProfileContent = (
    <div className="space-y-5">
      <SectionTitle kicker="MON PROFIL" title="Coordonnées" em="apporteur." />
      <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6 md:p-10 space-y-4">
        {[
          ['Statut juridique', profile.legal_status?.replace(/_/g, ' ')],
          ['Société', profile.company_name || '—'],
          ['SIRET', profile.siret || '—'],
          ['IBAN', profile.iban || 'Non renseigné'],
          ['Taux de commission', `${ratePercent} %`],
          ['Code partenaire', profile.share_code],
        ].map(([label, value]) => (
          <div key={label} className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-6 border-b border-[#1F2A4A]/8 pb-3 last:border-0">
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896] md:w-48">{label}</span>
            <span className="text-[#1F2A4A]">{value}</span>
          </div>
        ))}
        <p className="text-xs text-[#4A5568]/70 pt-3">
          Contacte l'équipe via <a href="mailto:assistance.sellerie@gmail.com" className="text-[#C9B896] underline-offset-4 hover:underline">assistance.sellerie@gmail.com</a> pour mettre à jour ton IBAN ou ton statut.
        </p>
      </div>
    </div>
  );

  const sections = [
    { key: 'overview', label: 'Vue d\'ensemble', Icon: TrendingUp, content: Overview },
    { key: 'leads', label: 'Mes candidats', Icon: Users, badge: leads.length, content: LeadsContent },
    { key: 'commissions', label: 'Commissions', Icon: Euro, badge: commissions.length, content: CommissionsContent },
    { key: 'profile', label: 'Mon profil', Icon: Briefcase, content: ProfileContent },
  ];

  return (
    <DashboardShell
      kicker="ESPACE APPORTEUR D'AFFAIRES"
      roleLabel={`Apporteur · ${ratePercent} % HT`}
      sections={sections}
      defaultSection="overview"
      topSlot={TopSlot}
      testIdRoot="apporteur-dashboard"
      nested
    />
  );
}
